import type { LocationState, UseLocationStateOptions } from '@ricoh/ria.client.core/hooks/app/router.type';

import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate as useNavigateCore } from 'react-router';
import { useFirstRenderLayoutEffect } from '@ricoh/ria.client.core/hooks/app/lifecycle';
import { Url } from '@ricoh/ria.client.core/utils/es/url';

import type { Navigate } from './router.type';

/**
 * Use LocationState.
 *
 * @template TState Data type.
 * @param {UseLocationStateOptions} options Options.
 *
 * @returns {TState | undefined} LocationState.
 */
export function useLocationState<TState = unknown>({ removeState = false }: UseLocationStateOptions = {}): LocationState<TState> | undefined {
  const location = useLocation<LocationState<TState>>();

  useFirstRenderLayoutEffect(() => {
    if (!removeState) {
      return;
    }

    if (!location.state) {
      return;
    }

    // * Remove state
    globalThis.history.replaceState({}, '');
  });

  return useMemo<LocationState<TState> | undefined>(() => location.state, [location.state]);
}

/**
 * Use Navigate with Timestamp.
 *
 * @returns {Navigate} Navigate with Timestamp.
 */
export function useNavigate(): Navigate {
  const navigate = useNavigateCore();

  return useCallback<Navigate>(
    (to, options) => {
      const state = Object.assign<LocationState>(options?.state ?? {}, {
        t: Date.now(),
      });

      const toRoute = to instanceof Url ? to.getPath() : to;
      return navigate(toRoute, {
        ...options,
        state,
      });
    },
    [navigate],
  );
}
